import { Link } from 'react-router';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE VISUAL VOCABULARY OF A NUMBER. [P6a; promoted ANALYTICS §5]
 *
 *  ⚠️ PROMOTED FROM `pages/Dashboard/tiles/parts.tsx` ON ITS SECOND USER. The Usage Analytics
 *  page ranks addresses, routes and places with the same list and states status classes with the
 *  same `<dl>`, and a second copy of either is a second place R1 and R2 can be got wrong. What is
 *  about a TILE — the big status number, the duty list and its scroll idiom — stayed behind.
 *
 *  ⚠️ STILL NO CHART LIBRARY, AND THE TIME-AXIS DECISION IS NOW TAKEN RATHER THAN DEFERRED. [P6a]
 *  kept one out because nothing had a time axis; the analytics page is the first thing that does,
 *  and it draws its series and its heatmap as CSS — `SeriesBars` and `UsageHoursGrid` beside the
 *  page — each with a sentence and a table carrying the numbers. A library would add weight to a
 *  bundle whose `vendor-antd` is already 393 KB, and would have to be taught both rules below.
 *
 *  `components/intake/CoverageBar.tsx` is the in-idiom precedent and its two rules govern
 *  every graphic here:
 *
 *    (R1) TEXTURE — OR LIGHTNESS — AS WELL AS HUE, so states stay separable in greyscale.
 *    (R2) THE BAR IS `aria-hidden` AND A REAL SENTENCE IS THE CONTENT. `role="img"` with the
 *         description as its label was explicitly rejected, because it puts the information
 *         where a SIGHTED colour-blind operator never gets it — the exact reader the rule is
 *         for.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/** A `<dl>` of label/figure pairs — `Attribute`'s shape, earning its keep. */
export const FigureList = ({
  rows,
  className,
}: {
  rows: readonly { key: string; label: ReactNode; value: string }[];
  className?: string;
}) => (
  <dl className={cn('divide-y divide-border', className)}>
    {rows.map((row) => (
      <div key={row.key} data-row={row.key} className="flex items-baseline justify-between gap-3 py-1.5">
        <dt className="min-w-0 text-sm text-muted-foreground">{row.label}</dt>
        <dd className="shrink-0 text-sm font-medium tabular-nums text-foreground">{row.value}</dd>
      </div>
    ))}
  </dl>
);

/**
 * ⚠️ [C3c.2] A ROW'S NAME MAY REACH ITS RECORD, AND IT IS A REAL `<a href>`.
 *
 * `NameCell`'s rule, applied to a tile body: anything that NAVIGATES renders an anchor, so
 * ⌘-click opens a tab, the destination shows in the status bar, and the link is copyable. A
 * `useNavigate` onClick gives up all three.
 *
 * ⚠️ ON A DASHBOARD TILE IT IS SAFE ONLY UNDER `drillReach: 'footer'`. [C2b §6a.1] is explicit
 * that a `'card'` tile stretches `after:absolute after:inset-0` over its whole body, which would
 * sit on top of every one of these anchors — "a tile whose rows hold buttons must be
 * `drillReach: 'footer'`". Do not give a row a link on a card-reach tile.
 */
export const RowLink = ({ href, title, children }: { href?: string; title: string; children: ReactNode }) =>
  href === undefined ? (
    <span className="min-w-0 flex-1 truncate text-sm text-foreground" title={title}>
      {children}
    </span>
  ) : (
    <Link
      to={href}
      className="min-w-0 flex-1 truncate text-sm text-foreground hover:text-primary hover:underline"
      title={title}
    >
      {children}
    </Link>
  );

/**
 * A ranked list with a proportional bar BEHIND each row.
 *
 * ⚠️ ONE HUE ONLY, so R1 needs no texture: LENGTH is the encoding, and length survives
 * greyscale untouched. Reaching for `accent`/`muted`/`secondary` as a "second wash" produces
 * two rows that look identical while reading in source as though they differed ([D4.4]).
 *
 * ⚠️ AND THE CLIENT NEVER RE-SORTS. The server orders; a client sort over a top-N orders N
 * rows and lies about the rest.
 *
 * ⚠️ [ANALYTICS §5] `display` IS WHAT THE ROW SAYS WHEN THE NUMBER WOULD LIE. A route whose 95th
 * percentile fell in the unbounded top latency bucket still needs a bar length, but "10,000"
 * beside it would state a measurement nobody took; the bar reads `value` and the text reads
 * `display`. Omitted, the row prints `value` exactly as before.
 */
export const RankedList = ({
  rows,
}: {
  rows: readonly { key: string; label: string; value: number; display?: string; caption?: string; href?: string }[];
}) => {
  const max = rows.reduce((highest, row) => Math.max(highest, row.value), 0);
  return (
    <ol className="flex flex-col gap-1">
      {rows.map((row) => (
        <li key={row.key} data-row={row.key} className="relative overflow-hidden rounded-sm px-2 py-1.5">
          <span
            aria-hidden
            /*
             * ⚠️ [C1h §2e] `bg-data-bar`, WHICH IS `bg-active` IN `standard`. A ranking bar is a
             * MAGNITUDE, not a status — which is the whole of why §2g permits colour here.
             *
             * ⚠️ [C2b §6a] This used to end "and forbids it on `NestedBar` two components up".
             * That component is GONE — `REQUIREMENT_FULFILMENT` was its only consumer and
             * became a per-arrangement LIST, so §9's no-dead-tooling rule took it with it. The
             * distinction it drew is unchanged and now lives against `CoverageBar`, which is
             * where a status-tinted, textured bar still belongs.
             */
            className="absolute inset-y-0 left-0 bg-data-bar"
            style={{ width: `${String(max > 0 ? (row.value / max) * 100 : 0)}%` }}
          />
          <span className="relative flex items-baseline justify-between gap-3">
            <RowLink href={row.href} title={row.label}>
              {row.label}
            </RowLink>
            <span className="shrink-0 text-sm font-medium tabular-nums text-foreground">
              {row.display ?? row.value.toLocaleString('en-IN')}
              {row.caption ? <span className="ml-1 text-xs text-muted-foreground">{row.caption}</span> : null}
            </span>
          </span>
        </li>
      ))}
    </ol>
  );
};
